import { Component } from "react";
import PageContext, { updatePageContextAsync, getPageContext } from "../../components/Common/PageContext";
import { setStateAsync } from "../../util/stateHelper";
import { withStyles } from "@mui/styles";
import { Container } from "@mui/material";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import SnackbarAlert from "../../components/Snackbar/SnackbarAlert";
import AdhocCreditRequestInit from "../../components/CreditRequest/AdhocCreditRequestInit";
import AdhocCreditRequestSubmit from "../../components/CreditRequest/AdhocCreditRequestSubmit";
import { getInitialCreditData, postAdhocCreditRequestAsync } from "../../services/apiHelper";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import { isFeatureEnabledForLocation } from "../../util/featureUtil";
import { AxiosResponse } from "axios";
const styles = (theme) => ({});

interface NewAdhocCreditRequestProps {
	classes?: any;
	graphProfile?: any;
}

interface NewAdhocCreditRequestState {
	isAlertSeverityError: boolean;
	showAlertModal: boolean;
	alertModalMessage: string;
	poNumber: string | null;
	invNumber: string | null;
	continueToSubmit: boolean;
	scannedBarcode: string;
	initialCreditData: any;
	showLoadingDialog: boolean;
	snackbarAlert: { isOpen: boolean; message: string; severity: string };
	reasons: any[];
	error?: string;
}

class NewAdhocCreditRequest extends Component<NewAdhocCreditRequestProps, NewAdhocCreditRequestState> {
	static contextType = PageContext;
	setStateAsync: (state: Partial<NewAdhocCreditRequestState>) => Promise<void>;
	updatePageContextAsync: (context: any) => Promise<void>;
	getPageContext: () => any;

	constructor(props: NewAdhocCreditRequestProps) {
		super(props);
		this.state = {
			isAlertSeverityError: false,
			showAlertModal: false,
			alertModalMessage: "",
			poNumber: null,
			invNumber: null,
			continueToSubmit: false,
			scannedBarcode: "",
			initialCreditData: null,
			showLoadingDialog: false,
			snackbarAlert: {
				isOpen: false,
				message: "",
				severity: "",
			},
			reasons: [],
		};
		this.setStateAsync = setStateAsync.bind(this);
		this.updatePageContextAsync = updatePageContextAsync.bind(this);
		this.getPageContext = getPageContext.bind(this);
	}

	isAdhocCreditRequestFeatureEnabled = () => {
		const {
			userLocation,
			featureConfiguration: { adhocCreditRequest },
		} = this.getPageContext();

		return isFeatureEnabledForLocation(userLocation, adhocCreditRequest);
	};

	handleContinueAsync = async (poNumber, invNumber) => {
		try {
			await this.setStateAsync({ showLoadingDialog: true });
			// Process flowchart: https://miro.com/app/board/o9J_lfXrBoA=/?moveToWidget=3458764518801474069
			const response = await getInitialCreditData(poNumber, invNumber);

			let { creditRequestReasons } = this.getPageContext();

			const initialCreditData = response.data;
			if (initialCreditData.isEditActive) {
				await this.setStateAsync({
					showAlertModal: true,
					alertModalMessage:
						"There is an active receive for this invoice number - credit must be created when this is finalised",
					isAlertSeverityError: false /* warning */,
				});
			} else {
				// will continue to next screen
				await this.setStateAsync({
					initialCreditData: initialCreditData,
					continueToSubmit: true,
					poNumber: poNumber,
					invNumber: invNumber,
					reasons: creditRequestReasons,
				});
			}
		} catch (ex) {
			if (ex.request?.status === 404) {
				// PO not found
				await this.setStateAsync({
					showAlertModal: true,
					alertModalMessage: ex.request.responseText,
					isAlertSeverityError: true,
				});
			}
		} finally {
			await this.setStateAsync({ showLoadingDialog: false });
		}
	};

	handleBackAsync = async () => {
		await this.setStateAsync({ poNumber: "", invNumber: "", continueToSubmit: false });
	};

	handleSubmitAsync = async (skuItems) => {
		try {
			const adhocCreditRequest = {
				purchaserOrderNumber: this.state.poNumber,
				invoiceNumber: this.state.invNumber,
				submittedByUserId: this.props.graphProfile.staffId,
				submittedByUserName: this.props.graphProfile.displayName,
				quantities: skuItems,
			};
			await this.setStateAsync({ showLoadingDialog: true });
			await postAdhocCreditRequestAsync(adhocCreditRequest);
			await this.setStateAsync({
				showLoadingDialog: false,
				snackbarAlert: {
					isOpen: true,
					message: "Credit request saved successfuly",
					severity: "success",
				},
			});
			await this.handleBackAsync();
		} catch {
			await this.setStateAsync({
				showAlertModal: true,
				alertModalMessage: "Unexpected error",
				isAlertSeverityError: true,
			});
		} finally {
			await this.setStateAsync({ showLoadingDialog: false });
		}
	};

	handleCloseAlertAsync = async () => {
		await this.setStateAsync({ showAlertModal: false, error: null, isAlertSeverityError: false });
	};

	async componentDidMount() {
		document.addEventListener("onScan", this.onScan);
	}

	async componentWillUnmount() {
		document.removeEventListener("onScan", this.onScan);
	}

	onScan = async (e) => {
		await this.setStateAsync({ scannedBarcode: "" });
		if (this.state.continueToSubmit) await this.setStateAsync({ scannedBarcode: e.data.BarcodeValue });
	};

	handleCloseSnackbarAlertAsync = async () => {
		this.setStateAsync({ snackbarAlert: { ...this.state.snackbarAlert, isOpen: false } });
	};

	render() {
		return this.isAdhocCreditRequestFeatureEnabled() ? (
			<Container disableGutters maxWidth="md">
				{!this.state.continueToSubmit && <AdhocCreditRequestInit onContinue={this.handleContinueAsync} />}
				{this.state.continueToSubmit && (
					<AdhocCreditRequestSubmit
						poNumber={this.state.poNumber}
						invNumber={this.state.invNumber}
						scannedBarcode={this.state.scannedBarcode}
						initialCreditData={this.state.initialCreditData}
						onCancel={this.handleBackAsync}
						onSubmit={this.handleSubmitAsync}
						reasons={this.state.reasons}
					/>
				)}

				<ModalWithActionButton
					title={this.state.isAlertSeverityError ? "Error" : "Warning"}
					alertType={this.state.isAlertSeverityError ? "error" : "warning"}
					primaryButtonName="Ok"
					onPrimaryButtonClick={() => this.handleCloseAlertAsync()}
					isOpen={this.state.showAlertModal}
					message={this.state.alertModalMessage}
				/>
				<LoadingDialog message={"Loading..."} isOpen={this.state.showLoadingDialog} />
				<SnackbarAlert
					open={this.state.snackbarAlert.isOpen}
					message={this.state.snackbarAlert.message}
					severity={this.state.snackbarAlert.severity}
					onClose={this.handleCloseSnackbarAlertAsync}
				/>
			</Container>
		) : (
			<p>This is not enabled at your store yet.</p>
		);
	}
}
export default withStyles(styles, { withTheme: true })(NewAdhocCreditRequest);
